<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { CheckCircle2, XCircle, AlertTriangle, X } from 'lucide-vue-next'

export interface CustomToastProps {
  type: 'success' | 'error' | 'warning'
  title: string
  description?: string
  duration?: number
  onClose?: () => void
}

const props = withDefaults(defineProps<CustomToastProps>(), {
  duration: 4000,
})

const visible = ref(true)
const progressWidth = ref(100)
let animationFrame: number | null = null
let startTime: number | null = null

const borderColor = computed(() => {
  switch (props.type) {
    case 'success':
      return '#10B981'
    case 'error':
      return '#EF4444'
    case 'warning':
      return '#F59E0B'
  }
  return '#10B981'
})

const iconComponent = computed(() => {
  switch (props.type) {
    case 'success':
      return CheckCircle2
    case 'error':
      return XCircle
    case 'warning':
      return AlertTriangle
  }
  return CheckCircle2
})

const iconColor = computed(() => {
  switch (props.type) {
    case 'success':
      return 'text-emerald-500'
    case 'error':
      return 'text-red-500'
    case 'warning':
      return 'text-amber-500'
  }
  return 'text-emerald-500'
})

const progressColor = computed(() => {
  switch (props.type) {
    case 'success':
      return '#10B981'
    case 'error':
      return '#EF4444'
    case 'warning':
      return '#F59E0B'
  }
  return '#10B981'
})

function startProgress() {
  startTime = performance.now()
  const animate = (now: number) => {
    if (!startTime) return
    const elapsed = now - startTime
    const remaining = Math.max(0, 100 - (elapsed / props.duration) * 100)
    progressWidth.value = remaining
    if (remaining > 0) {
      animationFrame = requestAnimationFrame(animate)
    } else {
      handleClose()
    }
  }
  animationFrame = requestAnimationFrame(animate)
}

function handleClose() {
  visible.value = false
  setTimeout(() => {
    props.onClose?.()
  }, 200)
}

onMounted(() => {
  startProgress()
})

onUnmounted(() => {
  if (animationFrame) {
    cancelAnimationFrame(animationFrame)
  }
})
</script>

<template>
  <div
    class="custom-toast"
    :class="{ 'custom-toast--visible': visible }"
    :style="{ borderLeftColor: borderColor }"
  >
    <!-- Icon -->
    <div class="flex-shrink-0">
      <component :is="iconComponent" :size="20" :class="iconColor" />
    </div>

    <!-- Content -->
    <div class="flex-1 min-w-0">
      <p class="text-sm font-semibold text-gray-800 leading-tight">{{ title }}</p>
      <p v-if="description" class="text-xs text-gray-500 mt-0.5 leading-tight">
        {{ description }}
      </p>
    </div>

    <!-- Close Button -->
    <button
      class="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
      @click="handleClose"
    >
      <X :size="14" />
    </button>

    <!-- Progress Bar -->
    <div class="custom-toast__progress">
      <div
        class="custom-toast__progress-bar"
        :style="{ width: progressWidth + '%', backgroundColor: progressColor }"
      />
    </div>
  </div>
</template>

<style scoped>
.custom-toast {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 3px solid;
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
  min-width: 300px;
  max-width: 420px;
  position: relative;
  overflow: hidden;
}

.custom-toast__progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: transparent;
}

.custom-toast__progress-bar {
  height: 100%;
  transition: width 0.1s linear;
  border-radius: 0 0 0 8px;
}

@media (max-width: 768px) {
  .custom-toast {
    width: 100%;
    min-width: 0;
    max-width: none;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 10px;
    box-shadow:
      0 8px 20px -8px rgb(15 23 42 / 0.28),
      0 2px 6px -3px rgb(15 23 42 / 0.18);
  }

  .custom-toast :deep(svg) {
    width: 18px;
    height: 18px;
  }

  .custom-toast p {
    line-height: 1.35;
  }
}
</style>
